import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { todayIsoParis } from '@lemontaigu/staff';
import {
  compareStayDay,
  deriveStayRollup,
  STAY_FIELD_LABELS_FR,
  summarizeStayComparison,
  type BoardDayFigures,
  type StayDayComparison,
  type StayFeedProvenance,
} from '@lemontaigu/pms-core';
import { usePms, usePmsScope } from './PmsProvider.js';
import { fmtDayShort, isoAddDays } from './logic.js';

// « Occupation » — the metrics feed's daily rollup, SHOWN BESIDE the board.
//
// `docs/METRICS-FEED-BRIEF.md` §7, phase M2. Its gate, verbatim: "Prove the
// derived figures match reality before trusting them." So this screen changes
// nothing. It writes nothing, it does not touch `board_days`, and the desk goes
// on typing the four numbers exactly as before. It only lets the two be looked
// at together, which is the only way to earn the right to switch the board over
// in M4.
//
// ── Why a comparison and not just a display ────────────────────────────────
//
// A rollup on its own is convincing and unfalsifiable — every number looks
// plausible because there is nothing to check it against. The board, for all
// its gaps, is what the hotel actually believed on the day. Putting them side
// by side is what makes a systematic error visible, and there is one specific
// error this is built to catch:
//
//   The brief named `breakfasts` and `half_boards` (§6.1) without saying which
//   DATE they fall on. Migration 0125 derived it from the activity windows the
//   board already drives, and the operator confirmed it on 2026-09-01 —
//   breakfast is the morning AFTER the night, dinner is the night of the stay
//   ("they eat the night they arrive"). Were a convention ever wrong, the meal
//   rows and only the meal rows would disagree systematically in one direction,
//   which is what the « par ligne » summary below is built to surface.
//
// ── What is deliberately absent ───────────────────────────────────────────
//
//   * Any control that writes. M4 owns the switch-over; a « corriger » button
//     here would be the silent-overwrite trap §6.2 exists to prevent.
//   * A verdict on whether the feed is trustworthy. The screen reports; the
//     operator decides. An automatic « ✓ safe to switch » would be this file
//     grading its own homework.

const RANGES = [
  { key: 'week', label: '7 jours', back: 0, forward: 6 },
  { key: 'fortnight', label: '15 jours', back: 3, forward: 11 },
  { key: 'month', label: '30 jours', back: 7, forward: 22 },
  { key: 'past', label: '30 jours passés', back: 30, forward: 0 },
  { key: 'season', label: '90 jours passés', back: 90, forward: 0 },
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

const pct = (v: number | null) => (v == null ? '—' : `${Math.round(v * 100)} %`);

const fmtStamp = (iso: string | null) => {
  if (!iso) return 'jamais';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export function Occupation() {
  const { stayFeed, boardFigures } = usePms();
  const { current } = usePmsScope();

  const [rangeKey, setRangeKey] = useState<RangeKey>('week');
  const [rows, setRows] = useState<StayDayComparison[]>([]);
  const [provenance, setProvenance] = useState<StayFeedProvenance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [asked, setAsked] = useState(false);

  const range = RANGES.find((r) => r.key === rangeKey) ?? RANGES[0];

  const load = useCallback(async () => {
    if (!current) return;
    setLoading(true);
    setError(null);

    const today = todayIsoParis();
    const from = isoAddDays(today, -range.back);
    const to = isoAddDays(today, range.forward);

    const [feed, prov] = await Promise.all([
      stayFeed.dailyRollup(current, from, to),
      stayFeed.provenance(current),
    ]);

    if (!feed.ok) {
      setError(feed.error.message);
      setRows([]);
      setLoading(false);
      return;
    }
    setProvenance(prov.ok ? prov.value : null);

    // A board that cannot be read is not a reason to hide the feed — the rollup
    // is still worth seeing. Say so, and compare against nothing.
    const board = await boardFigures.figures(from, to);
    let figures: BoardDayFigures[] = [];
    if (board.ok) figures = board.value;
    else setError(`Tableau de bord illisible (${board.error.message}) — relevé affiché seul.`);

    const byDate = new Map(figures.map((d) => [d.date, d]));
    setRows(deriveStayRollup(feed.value).map((d) => compareStayDay(d, byDate.get(d.date) ?? null)));
    setLoading(false);
  }, [boardFigures, current, range.back, range.forward, stayFeed]);

  useEffect(() => {
    void load();
  }, [load]);

  const summary = useMemo(() => summarizeStayComparison(rows), [rows]);

  const requestRefresh = async () => {
    if (!current) return;
    setAsking(true);
    const r = await stayFeed.requestRefresh(current, 'Occupation — comparaison M2');
    setAsking(false);
    if (r.ok) setAsked(true);
    else setError(r.error.message);
  };

  if (!current) return <p className="muted">Sélectionnez un établissement.</p>;

  return (
    <section className="pms-occupation">
      <header className="occ-head">
        <div>
          <h1>Occupation — relevé Vega</h1>
          <p className="muted">
            Lecture seule : rien ici n’écrit le tableau de bord. « Parties », « Nuit préc. » et
            les « + pot. » n’existent pas au tableau — information nouvelle, rien à comparer.
          </p>
          <p className="muted occ-note">
            ⚠ <strong>La saisie manuelle n’est pas une référence.</strong> Elle a été faite à la
            main, par des personnes différentes, souvent en retard ou pas du tout — c’est
            précisément le problème que ce relevé remplace. Un écart signale donc d’abord une
            saisie fausse, pas un relevé faux. Le relevé se vérifie contre Vega, jamais contre
            le tableau.
          </p>
          <p className="muted occ-note">
            ⚠ Les petits-déjeuners d’un jour sont ceux de la <strong>nuit précédente</strong>, pas
            des occupants du soir : un groupe qui part le matin déjeune ce jour-là. Comparez la
            colonne « Petits-déj. » à « Nuit préc. », jamais à « Occupation ».
          </p>
          <p className="muted occ-note">
            ⚠ Un <strong>⚠</strong> sur l’occupation signale une chambre comptée occupée
            alors que l’effectif Vega n’y loge personne — le plus souvent une chambre non
            « libérée ». Le relevé ne retire rien : la correction se fait dans Vega.
          </p>
          <p className="muted occ-note">
            ⚠ Les repas sont ceux <strong>servis</strong> (effectifs Vega), pas ceux vendus :
            un client en demi-pension qui saute le dîner garde son tarif, et un client en
            chambre seule peut être servi. Un <strong>*</strong> signale qu’au moins une
            réservation n’avait pas d’effectif saisi ce jour-là : son plan tarifaire a servi
            d’estimation. Le chiffre reste utilisable — il est simplement en partie prévu.
          </p>
        </div>
        <div className="occ-ranges">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className={r.key === rangeKey ? 'is-active' : ''}
              onClick={() => setRangeKey(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <Provenance
        provenance={provenance}
        asking={asking}
        asked={asked}
        onRefresh={() => void requestRefresh()}
      />

      {error && <p className="occ-error">{error}</p>}

      {loading ? (
        <p className="muted">Chargement…</p>
      ) : rows.length === 0 ? (
        <p className="muted">Aucun relevé sur cette période.</p>
      ) : (
        <>
          <Summary summary={summary} />
          <Table rows={rows} />
        </>
      )}
    </section>
  );
}

function Provenance({
  provenance,
  asking,
  asked,
  onRefresh,
}: {
  provenance: StayFeedProvenance | null;
  asking: boolean;
  asked: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="occ-provenance">
      <div>
        {/* §6.2 — the figures are shown WITH their timestamp. A number with no
            « as of » is the silent staleness this feed exists to remove. */}
        <span className="occ-stamp">
          Dernier import : <strong>{fmtStamp(provenance?.importedAt ?? null)}</strong>
        </span>
        {provenance?.sourceChangedAt && (
          <span className="occ-stamp muted">
            {' '}
            · données Vega du {fmtStamp(provenance.sourceChangedAt)}
          </span>
        )}
        {provenance?.windowStart && (
          <span className="occ-stamp muted">
            {' '}
            · couvre {provenance.windowStart} → {provenance.windowEnd}
          </span>
        )}
      </div>

      {provenance && !provenance.lastAttemptApplied && (
        // The distinction that matters: the numbers below are the last GOOD
        // import, not the last attempt. Saying only "last import: <date>" would
        // hide a feed that has been failing for a week behind a fresh-looking
        // timestamp.
        <p className="occ-refused">
          ⚠ Le dernier essai a été refusé — {provenance.refusalReason ?? 'raison non précisée'}. Les
          chiffres ci-dessous proviennent de l’import précédent.
        </p>
      )}

      <button type="button" onClick={onRefresh} disabled={asking}>
        {asking ? 'Demande en cours…' : 'Mettre à jour maintenant'}
      </button>
      {asked && (
        // Honest about what was actually achieved: nothing outside the hotel
        // LAN can call the importer, so this queued a request (migration 0122).
        <span className="muted"> Demande enregistrée — prise en compte sous une minute.</span>
      )}
    </div>
  );
}

function Summary({ summary }: { summary: ReturnType<typeof summarizeStayComparison> }) {
  return (
    <div className="occ-summary">
      <dl>
        <div>
          <dt>Jours comparés</dt>
          <dd>{summary.daysAgreeing + summary.daysDiffering}</dd>
        </div>
        <div>
          {/* NOT « Concordance ». That word makes the board the reference and
              the relevé the candidate, which is backwards: the operator
              confirmed 2026-09-01 that the manual figures « got consistently
              wrong ». This measures how often the desk happened to match, and
              is a statement about the SAISIE.
              `Previsionnel.tsx` already learned this once — its « Fidélité du
              modèle » panel was deleted a commit after shipping because
              "measuring reality against a baseline known to be invented yields
              confident numbers about nothing". */}
          <dt>Saisie identique</dt>
          {/* Null, not 100 %, when the board says nothing at all — a perfect
              score over an empty set would be the most misleading number here. */}
          <dd>{pct(summary.agreementRate)}</dd>
        </div>
        <div>
          <dt>Écarts</dt>
          <dd>{summary.daysDiffering}</dd>
        </div>
        <div>
          <dt>Tableau non saisi</dt>
          <dd>{summary.daysBoardSilent}</dd>
        </div>
      </dl>

      {summary.byField.length > 0 && (
        <div className="occ-byfield">
          <h2>Écarts par ligne</h2>
          <p className="muted">
            Des écarts dispersés = saisie manuelle approximative, ce qui est attendu. Une{' '}
            <strong>seule</strong> ligne en tête, toujours dans le même sens, est le signe
            contraire : une règle de calcul fausse ici.
          </p>
          <ul>
            {summary.byField.map((f) => (
              <li key={f.field}>
                <span>{STAY_FIELD_LABELS_FR[f.field]}</span>
                <span>
                  {f.days} jour{f.days > 1 ? 's' : ''}
                </span>
                <span className={f.totalDelta >= 0 ? 'delta-pos' : 'delta-neg'}>
                  {f.totalDelta >= 0 ? '+' : ''}
                  {f.totalDelta} cumulé
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Table({ rows }: { rows: StayDayComparison[] }) {
  const deltaFor = (r: StayDayComparison, field: string) =>
    r.deltas.find((d) => d.field === field) ?? null;

  /** A meal figure resting partly on the rate plan rather than on a recorded
      effectif. Not an error — the source writes no effectif row when there is
      nothing to serve — but the figure is then part measurement and part
      forecast, and a number that cannot say which is the kind this whole screen
      exists to stop shipping. */
  const estimated = (parties: number) =>
    parties > 0 ? (
      <span
        className="occ-estimated"
        title={`${parties} réservation(s) sans effectif saisi — estimé d'après le plan tarifaire`}
      >
        *
      </span>
    ) : null;

  const cell = (r: StayDayComparison, field: string, feedValue: number, extra?: ReactNode) => {
    const d = deltaFor(r, field);
    return (
      <td className={d ? 'occ-differ' : undefined}>
        <span className="occ-feed">{feedValue}</span>
        {extra}
        {d && (
          <span className="occ-board" title="Saisi au tableau de bord">
            {' '}
            ({d.board} · {d.delta! >= 0 ? '+' : ''}
            {d.delta})
          </span>
        )}
      </td>
    );
  };

  return (
    <table className="occ-table">
      <thead>
        <tr>
          <th>Jour</th>
          <th>Occupation</th>
          <th>Personnes</th>
          <th title="Réservations — un groupe ou une résa multi-chambres compte pour une">
            Parties
          </th>
          <th title="Clients ayant dormi la nuit précédente — c'est eux qui déjeunent ce matin, pas les occupants de ce soir">
            Nuit préc.
          </th>
          <th>Arrivées</th>
          <th>Départs</th>
          <th>Recouches</th>
          <th title="Servis le matin (effectifs Vega) — les clients de la NUIT PRÉCÉDENTE, pas ceux de ce soir">
            Petits-déj.
          </th>
          <th className="occ-pool-head" title="En plus : non servis au petit-déjeuner">+ pot.</th>
          <th>Demi-pens.</th>
          <th className="occ-pool-head" title="En plus : non servis en demi-pension">+ pot.</th>
          <th>État</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.date} className={r.verdict === 'differ' ? 'is-differ' : undefined}>
            <th scope="row">{fmtDayShort(r.date)}</th>
            {cell(r, 'occupancy', r.feed.roomsOccupied,
                  r.feed.roomsNoLodging > 0 ? (
                    <span
                      className="occ-empty-room"
                      title={`${r.feed.roomsNoLodging} chambre(s) comptée(s) occupée(s) alors que l'effectif Vega n'y loge personne — probablement non « libérée(s) ». À corriger dans Vega ; rien n'est retiré ici.`}
                    >
                      {' '}⚠
                    </span>
                  ) : null)}
            <td>
              {/* §5's whole argument: 42 rooms / 64 people and 33 rooms / 61
                  people are the same head count and a completely different
                  ménage load. The board has never carried this column, so
                  there is nothing to compare it against — it is new
                  information, not a discrepancy. */}
              {r.feed.guestsOccupied}
              {r.feed.guestsChildren > 0 && (
                <span className="muted"> ({r.feed.guestsChildren} enf.)</span>
              )}
            </td>
            <td className="muted" title="Réservations présentes cette nuit">
              {r.feed.partiesOccupied}
            </td>
            <td
              className="muted occ-lastnight"
              title="Clients ayant dormi la nuit précédente — la population des petits-déjeuners de ce matin"
            >
              {r.feed.guestsLastNight}
            </td>
            {cell(r, 'arrivals', r.feed.roomsArrivals)}
            {cell(r, 'departures', r.feed.roomsDepartures)}
            <td className="muted">{r.feed.roomsStayovers}</td>
            {cell(r, 'breakfasts', r.feed.breakfasts,
                  estimated(r.feed.breakfastFromPlanParties))}
            <td className="muted occ-pool" title="Clients de la nuit précédente non servis au petit-déjeuner — preneurs potentiels">
              {r.feed.guestsNoBreakfast > 0 ? `+${r.feed.guestsNoBreakfast}` : '—'}
            </td>
            {cell(r, 'halfBoards', r.feed.halfBoards,
                  estimated(r.feed.dinnerFromPlanParties))}
            <td className="muted occ-pool" title="Clients présents ce soir non servis en demi-pension — convives à la carte potentiels">
              {/* INDICATORS, never counts. These are the people who COULD take
                  the meal; most will not, and outside walk-ins are not in them
                  at all — so `diners` stays the desk's to type (§6.1). Rendered
                  muted and signed « + » so they read as a margin on the figure
                  beside them rather than as covers. */}
              {r.feed.guestsNoDinner > 0 ? `+${r.feed.guestsNoDinner}` : '—'}
            </td>
            <td>
              {r.verdict === 'boardSilent' ? (
                <span className="muted" title="Le tableau de bord n’a pas été saisi ce jour-là">
                  non saisi
                </span>
              ) : r.verdict === 'agree' ? (
                <span className="occ-ok">saisie idem</span>
              ) : (
                <span className="occ-warn">
                  {r.deltas.length} écart{r.deltas.length > 1 ? 's' : ''}
                </span>
              )}
              {r.feed.roomsUnallocated > 0 && (
                <span className="muted" title="Réservations OTA sans chambre attribuée">
                  {' '}
                  · {r.feed.roomsUnallocated} non attr.
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
